/**
 * `CredentialsSettings` — GVW-147 layer credentials health.
 *
 * Per brief §2 / §5: shows per-source connector health, last sync, expiration.
 * Phase-2 read-only view; rotation cross-pivots to platform Admin per brief §10.
 *
 * Persistence: n/a in Phase 2 (read-only); a small cached snapshot is kept
 * in localStorage under `gvw-set-credentials` so the offline-stale state
 * has something to render. Real health comes from `@geosoft/platform-sdk`
 * once the integration-health endpoint lands.
 *
 * TODO(WP-E11): swap mock data for `sdk.integrations.health(...)` and remove
 * the localStorage snapshot.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-147/brief.md
 */

import { useCallback, useEffect, useState } from 'react';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

type CredStatus = 'healthy' | 'degraded' | 'connected-error' | 'expired' | 'permission-denied' | 'quota-exceeded';

interface LayerCredential {
  layerId: string;
  layerName: string;
  providerName: string;
  status: CredStatus;
  lastSync: string;
  expiresAt: string;
}

const STORAGE_KEY = 'gvw-set-credentials';

const MOCK_CREDENTIALS: LayerCredential[] = [
  { layerId: 'l-001', layerName: 'Regional Survey', providerName: 'ESRI ArcGIS Online', status: 'healthy', lastSync: new Date(Date.now() - 15 * 60 * 1000).toISOString(), expiresAt: new Date(Date.now() + 30 * 86400 * 1000).toISOString() },
  { layerId: 'l-002', layerName: 'Geological Baseline', providerName: 'PostGIS', status: 'degraded', lastSync: new Date(Date.now() - 2 * 3600 * 1000).toISOString(), expiresAt: new Date(Date.now() + 5 * 86400 * 1000).toISOString() },
  { layerId: 'l-003', layerName: 'Mineral Rights', providerName: 'Oracle Spatial', status: 'expired', lastSync: new Date(Date.now() - 3 * 86400 * 1000).toISOString(), expiresAt: new Date(Date.now() - 86400 * 1000).toISOString() },
];

const STATUS_GLYPH: Record<CredStatus, { icon: string; label: string; color: string }> = {
  healthy: { icon: 'check_circle', label: 'Healthy', color: tokens.color.semantic.success },
  degraded: { icon: 'warning', label: 'Degraded', color: tokens.color.semantic.warning },
  'connected-error': { icon: 'error', label: 'Connection error', color: tokens.color.semantic.danger },
  expired: { icon: 'key', label: 'Expired', color: tokens.color.semantic.danger },
  'permission-denied': { icon: 'block', label: 'Admin only', color: tokens.color.semantic.danger },
  'quota-exceeded': { icon: 'monitoring', label: 'Quota exceeded', color: tokens.color.semantic.warning },
};

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const abs = Math.abs(diff);
  const minutes = Math.floor(abs / 60000);
  if (minutes < 60) return `${minutes}m ${diff >= 0 ? 'ago' : 'from now'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${diff >= 0 ? 'ago' : 'from now'}`;
  const days = Math.floor(hours / 24);
  return `${days}d ${diff >= 0 ? 'ago' : 'from now'}`;
}

export function CredentialsSettings() {
  const liveRegion = useLiveRegion();
  const [credentials, setCredentials] = useState<LayerCredential[]>([]);

  useEffect(() => {
    let next = MOCK_CREDENTIALS;
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) next = JSON.parse(stored) as LayerCredential[];
      else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(MOCK_CREDENTIALS));
    } catch {
      /* fall back to mock */
    }
    setCredentials(next);
  }, []);

  useEffect(() => {
    document.title = 'Layer credentials · GeoViewer';
    liveRegion.announce('status', 'Layer credentials health loaded.');
  }, [liveRegion]);

  const openInAdmin = useCallback(() => {
    liveRegion.announce('status', 'Opening Platform Admin to rotate credentials.');
  }, [liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="gvw-147-heading"
      data-gvw-id="GVW-147"
      data-route="/workspace/settings/credentials"
      style={{
        minHeight: '100%',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <h1
        id="gvw-147-heading"
        style={{
          margin: 0,
          marginBottom: tokens.space[4],
          fontSize: tokens.font.size['2xl'],
          fontWeight: tokens.font.weight.semibold,
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[3],
        }}
      >
        <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 28 }}>
          key
        </span>
        Layer credentials
      </h1>

      <p
        id="gvw-147-helper-masked"
        style={{ marginTop: 0, marginBottom: tokens.space[5], color: tokens.color.ink.secondary, fontSize: tokens.font.size.md }}
      >
        Credentials are masked. Open Platform Admin to view or rotate.
      </p>

      {credentials.length === 0 ? (
        <div style={{ padding: tokens.space[5], background: tokens.color.surface[1], borderRadius: tokens.radius.md, textAlign: 'center', color: tokens.color.ink.secondary }}>
          <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 32, display: 'block', marginBottom: tokens.space[2] }}>
            key
          </span>
          No layer credentials.
        </div>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fit, minmax(${tokens.layout.panelMd}px, 1fr))`,
            gap: tokens.space[3],
          }}
        >
          {credentials.map((cred) => {
            const glyph = STATUS_GLYPH[cred.status];
            return (
              <li
                key={cred.layerId}
                aria-describedby="gvw-147-helper-masked"
                style={{
                  padding: tokens.space[4],
                  background: tokens.color.surface[1],
                  border: tokens.border.thin,
                  borderRadius: tokens.radius.md,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: tokens.space[2],
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: tokens.space[2] }}>
                  <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18, color: glyph.color }}>
                    {glyph.icon}
                  </span>
                  <span style={{ fontSize: tokens.font.size.sm, color: glyph.color, fontWeight: tokens.font.weight.medium }}>
                    {glyph.label}
                  </span>
                </div>
                <div style={{ fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium }}>{cred.layerName}</div>
                <div style={{ fontSize: tokens.font.size.sm, color: tokens.color.ink.secondary }}>{cred.providerName}</div>
                <div style={{ fontSize: tokens.font.size.xs, color: tokens.color.ink.muted, display: 'flex', justifyContent: 'space-between' }}>
                  <span>Last sync {formatRelative(cred.lastSync)}</span>
                  <span>Expires {formatRelative(cred.expiresAt)}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div style={{ marginTop: tokens.space[5] }}>
        <button
          type="button"
          onClick={openInAdmin}
          style={{
            padding: `${tokens.space[3]}px ${tokens.space[5]}px`,
            background: tokens.color.accent.primary,
            color: tokens.color.accent.on,
            border: 'none',
            borderRadius: tokens.radius.md,
            fontSize: tokens.font.size.md,
            fontWeight: tokens.font.weight.medium,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
          }}
        >
          <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18 }}>
            open_in_new
          </span>
          Manage in Platform Admin
        </button>
      </div>
    </main>
  );
}
